import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, exhaustMap } from 'rxjs/operators';
import { of, tap } from 'rxjs';

import * as ReportActions from './report.actions';
import { ReportClientService } from '@mode/shared/data-access-webapp';
import { ToastService } from '@mode/shared/util-toast';
import { AnalyticsService } from '@mode/shared/util-segment';
import { createReportAndChartFromDatasetBrowserUrl } from './report.actions';

@Injectable()
export class ReportEffects {
  loadNewReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.editorRouteLoadNewReport),
      concatMap((action) => {
        return this.reportClient.loadNewReport(action.report);
      }),
      map((report) => ReportActions.apiLoadNewReportSuccess({ report })),
      catchError((err) => of(ReportActions.apiLoadNewReportFailure({ error: err })))
    );
  });

  loadReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ReportActions.editorRouteLoadReport,
        ReportActions.datasetViewRouteLoadReport,
        ReportActions.reportViewRouteLoadReport
      ),
      concatMap((action) => {
        return this.reportClient.loadSimpleReport(action.username, action.token);
      }),
      map((report) => ReportActions.apiLoadReportSuccess({ report })),
      catchError((err) => of(ReportActions.apiLoadReportFailure({ error: err })))
    );
  });

  loadReportViewContainerHeaderData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.loadReportViewContainerHeaderData),
      concatMap((action) =>
        this.reportClient.loadHeaderReportData(action.token, [
          'new_embed_key',
          'new_report_schedule',
          'new_report_subscription',
          'new_star',
          'space',
        ])
      ),
      map((headerReportData) => ReportActions.loadReportViewContainerHeaderDataSuccess({ headerReportData })),
      catchError((err) => of(ReportActions.loadReportViewContainerHeaderDataFailure({ error: err })))
    );
  });

  loadRestrictedReportViewContainerHeaderData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.loadRestrictedReportViewContainerHeaderData),
      concatMap(() => this.reportClient.loadRestrictedHeaderReportData()),
      map((headerReportData) => ReportActions.loadRestrictedReportViewContainerHeaderDataSuccess({ headerReportData })),
      catchError((err) => of(ReportActions.loadRestrictedReportViewContainerHeaderDataFailure({ error: err })))
    );
  });

  loadWorkspaceHeaderData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.loadWorkspaceHeaderData),
      concatMap((action) => {
        return this.reportClient.loadHeaderReportData(action.token, [
          'new_embed_key',
          'new_report_schedule',
          'new_report_subscription',
          'new_space_link',
          'space',
        ]);
      }),
      map((headerReportData) => ReportActions.loadWorkspaceHeaderDataSuccess({ headerReportData })),
      catchError((err) => of(ReportActions.loadWorkspaceHeaderDataFailure({ error: err })))
    );
  });

  loadDatasetWorkspaceHeaderData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.loadDatasetWorkspaceHeaderData),
      concatMap((action) =>
        this.reportClient.loadHeaderReportData(action.token, [
          'new_report_run',
          'new_report_schedule',
          'new_report_subscription',
          'space',
        ])
      ),
      map((headerReportData) => ReportActions.loadDatasetWorkspaceHeaderDataSuccess({ headerReportData })),
      catchError((err) => of(ReportActions.loadDatasetWorkspaceHeaderDataFailure({ error: err })))
    );
  });

  apiRenameReportSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportActions.apiRenameReportSuccess),
        tap(() => this._toastService.success('Changes saved'))
      );
    },
    { dispatch: false }
  );

  apiRenameReportFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportActions.apiRenameReportFailure),
        tap(() => this._toastService.error('There was a problem saving'))
      );
    },
    { dispatch: false }
  );

  renameReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.editorDialogRenameReport),
      concatMap((action) => {
        return this.reportClient.saveReport(action.token, action.name, action.description);
      }),
      map((report) => ReportActions.apiRenameReportSuccess({ report })),
      catchError((err) => of(ReportActions.apiRenameReportFailure({ error: err })))
    );
  });

  createReportAndChartWithDataset$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          ReportActions.createReportAndChartFromDatasetEditor,
          ReportActions.createReportAndChartFromDatasetView,
          ReportActions.createReportAndChartFromDatasetBrowserSearch,
          ReportActions.createReportAndChartFromDatasetBrowserUrl
        ),
        exhaustMap((action) => {
          return this.reportClient
            .createReportAndChartWithDataset(
              action.switchViewToken,
              action.chartColorPaletteToken,
              action.encoding,
              action.format,
              action.fieldFormats
            )
            .pipe(
              map((response) => ({ accountId: action.accountId, token: response.token, vizToken: response.viz_token }))
            );
        }),
        map((response) => {
          this._window.location.pathname = `/editor/${response.accountId}/reports/${response.token}/viz/${response.vizToken}`;
        }),
        catchError(() => {
          this._toastService.error('Something went wrong');
          return of(null);
        })
      );
    },
    { dispatch: false }
  );

  submitAnalytics$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportActions.submitUseDatasetAnalytics),
        map((action) =>
          this.analyticsService.run(action.runMethodType, action.analyticEventName, {
            location: action.location,
            action: action.actionType,
            context: action.context,
            context_id: action.context_id,
          })
        )
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private analyticsService: AnalyticsService,
    private reportClient: ReportClientService,
    private _window: Window,
    private _toastService: ToastService
  ) {}
}
